import React from 'react';
import {
  Paper, Typography, FormControl, InputLabel, Select, MenuItem, OutlinedInput,
  Divider, TextField, Chip, Box, Stack, Tooltip, Button, CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Collapse from '@mui/material/Collapse';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { subjects, UNIVERSAL_TOOLS } from './teachingChatUtils';
import type { Message } from './teachingChatUtils';

export interface ContextualSuggestion {
  label: string;
  priority?: number;
  icon?: React.ReactNode;
}

export interface TeachingPanelProps {
  gradeLevel: string;
  subject: string;
  updateSettings: (patch: { gradeLevel?: string; subject?: string; standardIds?: string[] }) => void;
  loadingGradeLevels: boolean;
  availableGradeLevels: string[];
  selectedStrand: string;
  setSelectedStrand: (value: string) => void;
  availableStrands: string[];
  selectedStandards: string[];
  setSelectedStandards: (ids: string[]) => void;
  strandFilteredStandards: { _id: string; code?: string; standard?: string; description?: string; short_description?: string }[];
  capitalizeFirstLetter: (str: string | undefined | null) => string;
  loadingStandards: boolean;
  standardLoading: boolean;
  messages: Message[];
  personalizationText: string;
  aiSuggestLoading: boolean;
  prioritizedContextualSuggestions: ContextualSuggestion[];
  visibleContextualSuggestions: ContextualSuggestion[];
  hiddenContextualSuggestions: ContextualSuggestion[];
  hasMoreContextual: boolean;
  showMoreContextual: boolean;
  setShowMoreContextual: (value: boolean) => void;
  setInput: (value: string) => void;
  setAiSuggested: React.Dispatch<React.SetStateAction<string[]>>;
}

const TeachingPanel: React.FC<TeachingPanelProps> = ({
  gradeLevel,
  subject,
  updateSettings,
  loadingGradeLevels,
  availableGradeLevels,
  selectedStrand,
  setSelectedStrand,
  availableStrands,
  selectedStandards,
  setSelectedStandards,
  strandFilteredStandards,
  capitalizeFirstLetter,
  loadingStandards,
  standardLoading,
  messages,
  personalizationText,
  aiSuggestLoading,
  prioritizedContextualSuggestions,
  visibleContextualSuggestions,
  hiddenContextualSuggestions,
  hasMoreContextual,
  showMoreContextual,
  setShowMoreContextual,
  setInput,
  setAiSuggested,
}) => (
  <Paper
    elevation={1}
    sx={{ p: 2, display: { xs: 'none', md: 'block' }, height: '100%', minHeight: 0, overflow: 'auto', borderTopRightRadius: 0 }}
  >
    <Typography variant="h6" gutterBottom>
      Teaching Context
    </Typography>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel id="grade-label">Grade Level</InputLabel>
      <Select
        labelId="grade-label"
        value={gradeLevel}
        label="Grade Level"
        onChange={(e) => updateSettings({ gradeLevel: e.target.value })}
        input={<OutlinedInput label="Grade Level" />}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {loadingGradeLevels ? (
          <MenuItem value="" disabled>
            <em>Loading grade levels...</em>
          </MenuItem>
        ) : availableGradeLevels.length === 0 && subject ? (
          <MenuItem value="" disabled>
            <em>No grade levels available</em>
          </MenuItem>
        ) : (
          availableGradeLevels.map((g) => (
            <MenuItem key={g} value={g}>
              {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].includes(g) ? `Grade ${g}` : g}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
    <FormControl fullWidth sx={{ mb: 2 }}>
      <InputLabel id="subject-label">Subject</InputLabel>
      <Select
        labelId="subject-label"
        value={subject}
        label="Subject"
        onChange={(e) => updateSettings({ subject: e.target.value })}
        input={<OutlinedInput label="Subject" />}
      >
        {subjects.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <Typography variant="subtitle2" gutterBottom>
      Learning Standards
    </Typography>
    <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={!gradeLevel}>
      <InputLabel>Content Strand</InputLabel>
      <Select
        value={selectedStrand}
        onChange={(e) => {
          setSelectedStrand(e.target.value);
          setSelectedStandards([]);
          updateSettings({ standardIds: [] });
        }}
        label="Content Strand"
        input={<OutlinedInput label="Content Strand" />}
      >
        <MenuItem value="All">All Strands</MenuItem>
        {availableStrands.map((strand) => (
          <MenuItem key={strand} value={strand}>
            {strand}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    <Autocomplete
      multiple
      options={strandFilteredStandards}
      getOptionLabel={(option: { standard?: string; code?: string; description?: string; short_description?: string }) => {
        if (!option) return '';
        const code = option.standard || option.code || 'N/A';
        const desc = option.description || option.short_description || 'Standard';
        return `${code} - ${capitalizeFirstLetter(desc)}`;
      }}
      isOptionEqualToValue={(option: { _id: string }, value: { _id: string }) => {
        if (!option || !value) return false;
        return option._id === value._id;
      }}
      value={strandFilteredStandards.filter((s) => selectedStandards.includes(s._id))}
      onChange={(_event, newValue) => {
        const newIds = newValue.map((s) => s._id);
        setSelectedStandards(newIds);
        updateSettings({ standardIds: newIds });
      }}
      disableCloseOnSelect
      fullWidth
      size="small"
      disabled={!gradeLevel}
      sx={{ mb: 2 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Learning Standards (Optional)"
          placeholder={selectedStandards.length === 0 ? 'Search for standards...' : ''}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingStandards || standardLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option._id}
            label={option.code || option.standard}
            size="small"
            sx={{ fontSize: '0.7rem', height: '20px' }}
          />
        ))
      }
    />

    <Divider sx={{ my: 2 }} />

    {messages.length > 0 && (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            fontSize: '1rem',
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <LightbulbIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
          Suggested follow-up prompts
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          Based on your last request
        </Typography>

        {aiSuggestLoading && prioritizedContextualSuggestions.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Generating suggestions…
            </Typography>
          </Box>
        )}

        <Stack spacing={1}>
          {visibleContextualSuggestions.map((suggestion, idx) => (
            <Tooltip key={`contextual-${idx}-${suggestion.label}`} title={suggestion.label} placement="left" arrow>
              <Box
                onClick={() => {
                  setInput(suggestion.label);
                  setAiSuggested((prev) => prev.filter((x) => x !== suggestion.label));
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(249, 115, 22, 0.08)',
                    borderColor: 'secondary.light',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                <Box
                  sx={{
                    color: 'secondary.main',
                    mt: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    backgroundColor: 'rgba(249, 115, 22, 0.1)',
                    flexShrink: 0,
                  }}
                >
                  {suggestion.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {suggestion.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {personalizationText}
                  </Typography>
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Stack>

        {hasMoreContextual && (
          <>
            <Collapse in={showMoreContextual}>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {hiddenContextualSuggestions.map((suggestion, idx) => (
                  <Tooltip
                    key={`contextual-hidden-${idx}-${suggestion.label}`}
                    title={suggestion.label}
                    placement="left"
                    arrow
                  >
                    <Box
                      onClick={() => {
                        setInput(suggestion.label);
                        setAiSuggested((prev) => prev.filter((x) => x !== suggestion.label));
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(249, 115, 22, 0.08)',
                          borderColor: 'secondary.light',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color: 'secondary.main',
                          mt: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 1,
                          backgroundColor: 'rgba(249, 115, 22, 0.1)',
                          flexShrink: 0,
                        }}
                      >
                        {suggestion.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {suggestion.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {personalizationText}
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
            <Button
              size="small"
              onClick={() => setShowMoreContextual(!showMoreContextual)}
              startIcon={showMoreContextual ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                mt: 1,
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'grey.100' },
              }}
            >
              {showMoreContextual ? 'Show less' : `Show ${hiddenContextualSuggestions.length} more`}
            </Button>
          </>
        )}

        {!aiSuggestLoading && prioritizedContextualSuggestions.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Send a message to get personalized suggestions
          </Typography>
        )}
      </Box>
    )}

    {messages.length > 0 && (
      <Box
        sx={{
          my: 2,
          borderTop: '1px solid',
          borderColor: 'grey.200',
          pt: 2,
        }}
      />
    )}

    <Box>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          fontSize: '1rem',
          mb: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AutoFixHighIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        Quick tools
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
        {gradeLevel || subject
          ? `For your ${gradeLevel ? `Grade ${gradeLevel}` : ''}${gradeLevel && subject ? ' ' : ''}${subject || ''} class`
          : 'Select grade and subject for personalized results'}
      </Typography>

      <Stack spacing={1}>
        {UNIVERSAL_TOOLS.map((tool) => (
          <Tooltip key={tool.id} title={tool.description || tool.label} placement="left" arrow>
            <Box
              onClick={() => setInput(tool.label)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                  borderColor: 'primary.light',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
            >
              <Box
                sx={{
                  color: 'primary.main',
                  mt: 0.25,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  backgroundColor: 'rgba(37, 99, 235, 0.1)',
                  flexShrink: 0,
                }}
              >
                {tool.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {tool.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                  {personalizationText}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  </Paper>
);

export default TeachingPanel;
